/**
 * Tiny typed fetch wrapper.
 *
 * BASE_URL = `${VITE_API_BASE_URL}/v1`. In dev the env var is empty so calls
 * hit same-origin `/v1/*` — the Vite dev server proxies these to the local API
 * at http://localhost:8080. In production, Caddy on the `web` Railway service
 * reverse-proxies `/v1/*` to the internal `api` service, so empty base is
 * correct there too. For E2E against a remote API, set VITE_API_BASE_URL to a
 * full origin.
 *
 * credentials: 'include' so the Better Auth session cookie rides along.
 */

const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const BASE_URL = `${API_ORIGIN}/v1`;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
};
